import { lazy, Suspense, type ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from './i18n';
import { useTheme } from './hooks/useTheme';
import { useSession } from './hooks/useSession';
import { Shell, type NavTab } from './components/Shell';
import { ListSkeleton } from './components/ui';
import { HomePage } from './pages/HomePage';
import { SearchPage } from './pages/SearchPage';
import { RestaurantPage } from './pages/RestaurantPage';
import { AccountPage } from './pages/AccountPage';
import { ProfilePage } from './pages/ProfilePage';
import { AddressesPage } from './pages/AddressesPage';
import { SignInPage } from './pages/SignInPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderPage } from './pages/OrderPage';
import { OrdersPage } from './pages/OrdersPage';
import { PayReturnPage } from './pages/PayReturnPage';
import { WalletPage } from './pages/WalletPage';
import { HelpPage } from './pages/HelpPage';
import { HelpNewPage } from './pages/HelpNewPage';
import { HelpChatPage } from './pages/HelpChatPage';
import { MyReviewsPage } from './pages/MyReviewsPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { InboxPage } from './pages/InboxPage';
import { NotifyPrefsPage } from './pages/NotifyPrefsPage';
import { ReferPage } from './pages/ReferPage';
import { CampusPassPage } from './pages/CampusPassPage';
import { LegalPage } from './pages/LegalPage';
import { LegalGate } from './pages/LegalGate';
import { UpdateGate } from './pages/UpdateGate';
import { IntegrityGate } from './pages/IntegrityGate';
import { DeepLinkRedirect, QrEntryPage } from './pages/LinkPages';
import { useAppRuntime } from './hooks/useAppRuntime';
import { CartBar } from './components/CartBits';
import { cartSummary, useCart } from './hooks/useCart';
import { money } from './utils/money';

// The address page carries the map library; load it only when needed.
const AddressFormPage = lazy(() =>
  import('./pages/AddressFormPage').then((module) => ({ default: module.AddressFormPage })),
);

const TAB_ROOTS = ['/', '/search', '/account'];

/** Signed-in-only pages send guests to sign-in and back. */
function RequireCustomer({ children }: { children: ReactElement }) {
  const session = useSession();
  const location = useLocation();
  if (session.loading) {
    return (
      <div className="px-4 pt-8">
        <ListSkeleton rows={3} />
      </div>
    );
  }
  if (!session.signedIn) {
    // Keep the query (e.g. /pay/return?intent=…&gw=…) so nothing is lost across sign-in.
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }
  return (
    <Suspense
      fallback={
        <div className="px-4 pt-8">
          <ListSkeleton rows={3} />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export function App() {
  const { t } = useI18n();
  useTheme();
  // W18: entry capture (?ref / QR scan), push registration + taps, cart-left-behind signal.
  useAppRuntime({
    codeApplied: (code) => t('growth.codeApplied', { code }),
    offerReady: (code) => t('growth.offerReady', { code }),
  });
  const location = useLocation();
  const navigate = useNavigate();
  const cart = useCart();
  const summary = cartSummary(cart);
  const onTabRoot = TAB_ROOTS.includes(location.pathname);
  const tabs: NavTab[] = [
    { to: '/', label: t('nav.home'), icon: 'home' },
    { to: '/search', label: t('nav.search'), icon: 'search' },
    { to: '/account', label: t('nav.account'), icon: 'user' },
  ];

  return (
    <Shell tabs={tabs} showNav={onTabRoot} raised={cart !== null && onTabRoot}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/r/:outletId" element={<RestaurantPage />} />
        <Route path="/r/:outletId/reviews" element={<ReviewsPage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/cart" element={<CheckoutPage />} />
        <Route
          path="/orders/:orderId"
          element={
            <RequireCustomer>
              <OrderPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/orders"
          element={
            <RequireCustomer>
              <OrdersPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/pay/return"
          element={
            <RequireCustomer>
              <PayReturnPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/wallet"
          element={
            <RequireCustomer>
              <WalletPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/help"
          element={
            <RequireCustomer>
              <HelpPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/help/new"
          element={
            <RequireCustomer>
              <HelpNewPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/help/:ticketId"
          element={
            <RequireCustomer>
              <HelpChatPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/reviews"
          element={
            <RequireCustomer>
              <MyReviewsPage />
            </RequireCustomer>
          }
        />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/account/legal/:slug" element={<LegalPage />} />
        <Route
          path="/account/profile"
          element={
            <RequireCustomer>
              <ProfilePage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/addresses"
          element={
            <RequireCustomer>
              <AddressesPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/address/new"
          element={
            <RequireCustomer>
              <AddressFormPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/address/:id/edit"
          element={
            <RequireCustomer>
              <AddressFormPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/inbox"
          element={
            <RequireCustomer>
              <InboxPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/notifications"
          element={
            <RequireCustomer>
              <NotifyPrefsPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/refer"
          element={
            <RequireCustomer>
              <ReferPage />
            </RequireCustomer>
          }
        />
        <Route
          path="/account/campus-pass"
          element={
            <RequireCustomer>
              <CampusPassPage />
            </RequireCustomer>
          }
        />
        <Route path="/q/:slug" element={<QrEntryPage />} />
        {[
          '/order/*',
          '/restaurant/*',
          '/wallet',
          '/help/*',
          '/inbox',
          '/refer',
          '/campus-pass',
          '/offers',
          '/home',
        ].map((path) => (
          <Route key={path} path={path} element={<DeepLinkRedirect />} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {cart && onTabRoot && (
        <CartBar
          countText={
            summary.count === 1 ? t('cart.item') : t('cart.items', { count: summary.count })
          }
          amountText={money(cart.country, summary.estimateMinor)}
          outletName={cart.outletName}
          actionLabel={t('cart.view')}
          aboveNav
          onOpen={() => navigate('/cart')}
        />
      )}
      {/* W21: blocking accept sheet after a material change to the terms (signed-in only). */}
      <LegalGate />
      {/* W23: update sheet when this build is unsupported (blocking) or a newer one is out. */}
      <UpdateGate />
      <IntegrityGate />
    </Shell>
  );
}
